import {
  useMutation,
  useQuery,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';
import { messagesKey } from '../constants';
import {
  structuredDataToBlock,
  toCandidateProfilesBlock,
  wantsFullProfiles,
} from '../helpers';
import type { ServerMessageInput } from '../stores/agentForgeStore';
import type { BlockOutput } from '../types';
import { callAgentForge } from './http';
import type {
  ExecuteActionInput,
  ExecuteActionResponse,
  ListMessagesArgs,
  MessagesResponse,
  SendMessageInput,
  SendMessageResponse,
  ServerMessage,
  UseMessagesArgs,
} from './messages.types';

const MESSAGES_STALE_TIME_MS = 1_000;

// Hide internal turns that shouldn't render as their own bubble. Legacy
// `/agentforge` does the same: tool outputs and empty intermediate
// assistant turns are noise — the final assistant prose already
// summarises whatever happened, so showing them double-renders.
const isVisibleMessage = (m: ServerMessage): boolean => {
  if (m.role === 'tool' || m.role === 'system') return false;
  if (m.role === 'assistant' && !m.content?.trim()) return false;
  return true;
};

// Extract a renderable BlockOutput from a tool message. Tool messages carry
// the rich payload on `metadata.structured_data`; we run it through the
// same mapper the WS path uses so refreshed sessions look identical to
// live-streamed ones.
const blockFromToolMessage = (m: ServerMessage): BlockOutput | null => {
  if (m.role !== 'tool') return null;
  const meta = m.metadata as
    | { structured_data?: unknown; tool_call_id?: unknown }
    | undefined;
  if (!meta?.structured_data) return null;
  const toolCallId =
    typeof meta.tool_call_id === 'string' ? meta.tool_call_id : undefined;
  return structuredDataToBlock(meta.structured_data, toolCallId);
};

// Walk the transcript in order. Accumulate any blocks from tool messages
// since the last visible turn, then attach them to the next assistant
// turn so its `outputs` includes both the prose summary AND the rich
// blocks the tool produced. Mirrors what commitStreamingDraft does on
// the live WS path.
export const toStoreInputs = (
  messages: ServerMessage[]
): ServerMessageInput[] => {
  const out: ServerMessageInput[] = [];
  let pendingBlocks: BlockOutput[] = [];
  // INTERIM: last user message drives the full-profile carousel promotion.
  let lastUserText = '';
  for (const m of messages) {
    if (m.role === 'tool') {
      let block = blockFromToolMessage(m);
      if (block?.type === 'candidate' && wantsFullProfiles(lastUserText)) {
        block = toCandidateProfilesBlock(block);
      }
      if (block) pendingBlocks.push(block);
      continue;
    }
    if (!isVisibleMessage(m)) {
      // e.g. an empty assistant turn that was just a tool-call decision.
      // Pending blocks survive — they belong to the *final* assistant
      // turn that summarises the tool output, not to this empty one.
      continue;
    }
    if (m.role === 'user') {
      pendingBlocks = [];
      lastUserText = m.content;
      out.push({
        id: m.id,
        role: 'user',
        content: m.content,
        createdAt: m.created_at,
      });
      continue;
    }
    // Assistant turn with text. Pair it with any blocks the tools
    // produced since the previous visible turn.
    const outputs: BlockOutput[] = [];
    if (m.content) {
      outputs.push({ type: 'text', data: { content: m.content } });
    }
    outputs.push(...pendingBlocks);
    pendingBlocks = [];
    out.push({
      id: m.id,
      role: 'assistant',
      content: m.content,
      createdAt: m.created_at,
      outputs,
    });
  }
  // Orphan blocks at end-of-transcript: typical when the agent finished a
  // turn by emitting a card-only tool (propose_clarify) and stopping with
  // no prose to anchor them to. Synthesize a trailing assistant turn that
  // carries them so hydrate doesn't silently drop the picker/results.
  if (pendingBlocks.length > 0) {
    const lastTool = [...messages].reverse().find((m) => m.role === 'tool');
    const anchorId = lastTool?.id ?? `assistant-orphan-${Date.now()}`;
    const createdAt = lastTool?.created_at ?? new Date().toISOString();
    out.push({
      id: `assistant-${anchorId}`,
      role: 'assistant',
      content: '',
      createdAt,
      outputs: pendingBlocks,
    });
  }
  return out;
};

// Polling client — no normalization. The BE returns flat `content` strings;
// the adapter from server shape → store TranscriptMessage lives in the
// agentforge store (`hydrateMessages`).
const listMessages = async ({
  sessionId,
  limit = 100,
  offset = 0,
  signal,
}: ListMessagesArgs): Promise<ServerMessage[]> => {
  const body = await callAgentForge<MessagesResponse>({
    url: `/sessions/${sessionId}/messages`,
    params: { limit, offset },
    signal,
  });
  return body.messages ?? [];
};

const sendMessage = (input: SendMessageInput): Promise<SendMessageResponse> =>
  callAgentForge<SendMessageResponse>({
    method: 'POST',
    url: `/sessions/${input.sessionId}/messages`,
    body: { message: input.message, timezone: input.timezone },
  });

const executeAction = (
  input: ExecuteActionInput
): Promise<ExecuteActionResponse> =>
  callAgentForge<ExecuteActionResponse>({
    method: 'POST',
    url: `/sessions/${input.sessionId}/actions`,
    body: { action: input.action, params: input.params },
  });

export const useMessagesQuery = (
  args: UseMessagesArgs
): UseQueryResult<ServerMessage[], Error> => {
  const sessionId = args.sessionId ?? '';
  const canRun = (args.enabled ?? true) && sessionId.length > 0;
  return useQuery({
    queryKey: [
      ...messagesKey(sessionId),
      { limit: args.limit, offset: args.offset },
    ],
    queryFn: ({ signal }) =>
      listMessages({
        sessionId,
        limit: args.limit,
        offset: args.offset,
        signal,
      }),
    enabled: canRun,
    staleTime: MESSAGES_STALE_TIME_MS,
    refetchInterval: args.refetchIntervalMs ?? false,
  });
};

export const useSendMessageMutation = (): UseMutationResult<
  SendMessageResponse,
  Error,
  SendMessageInput
> => useMutation({ mutationFn: sendMessage });

export const useExecuteActionMutation = (): UseMutationResult<
  ExecuteActionResponse,
  Error,
  ExecuteActionInput
> => useMutation({ mutationFn: executeAction });
